import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  Typography, 
  Tooltip,
  CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTenant } from '../contexts/TenantContext';

export const TenantSelector: React.FC = () => {
  const { availableTenants, currentTenant, loading, switchTenant } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (currentTenant) {
      console.log('TenantSelector - Current tenant:', currentTenant);
    }
    if (availableTenants.length > 0) {
      console.log('TenantSelector - Available tenants:', availableTenants);
    }
  }, [currentTenant, availableTenants]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantChange = (tenantCode: string) => {
    switchTenant(tenantCode);
    handleClose();
  };

  const getTenantDisplayName = (tenant: any) => {
    if (!tenant) return 'No tenant';
    return tenant.name || tenant.code;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading tenants...</Typography>
      </Box>
    );
  }

  if (!currentTenant) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="disabled" />
        <Typography variant="body2" color="text.secondary">No tenant available</Typography>
      </Box>
    );
  }

  if (availableTenants.length <= 1) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="primary" />
        <Typography variant="body2">
          {getTenantDisplayName(currentTenant)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Tooltip title="Switch tenant">
        <Button
          id="tenant-selector-button"
          aria-controls={open ? 'tenant-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
          startIcon={<BusinessIcon />}
          sx={{
            textTransform: 'none',
            color: '#1C5195',
          }}
        >
          {getTenantDisplayName(currentTenant)}
        </Button>
      </Tooltip>
      <Menu
        id="tenant-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'tenant-selector-button',
        }}
      >
        {availableTenants.map((tenant) => (
          <MenuItem 
            key={tenant.code}
            onClick={() => handleTenantChange(tenant.code)}
            selected={currentTenant?.code === tenant.code}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color={tenant.isDefault ? "secondary" : "inherit"} />
              <Box>
                <Typography component="span">
                  {getTenantDisplayName(tenant)}
                </Typography>
                {tenant.isDefault && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    (Default)
                  </Typography>
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};